// Explosions (RED-8). PURE (ENG-1).
//
// A deterministic ray-based block-destruction model (creeper power 3, TNT 4, …).
// Rays cast from the blast centre through the faces of a 16³ grid lose intensity
// to travel and to each block's blast resistance; a block whose ray still has
// intensity left is destroyed (unless indestructible). No RNG — the crater is a
// pure function of (centre, power, world), so the RED-8 golden is stable.
export interface Explodable {
  getBlockState(x: number, y: number, z: number): number;
  /** Blast resistance of the block at (x,y,z); Infinity = indestructible (bedrock). */
  blastResistance(x: number, y: number, z: number): number;
  /** Remove the block at (x,y,z) (host: air + relight + remesh; sim: to air). */
  breakBlock(x: number, y: number, z: number): void;
}

const STEP = 0.3;
const TRAVEL_DECAY = 0.225; // 0.3 · 0.75, per step

/** Compute the destroyed-block set for an explosion and apply it. Returns the set
 *  of `x,y,z` keys destroyed (for tests/goldens). `mobGriefing=false` → no blocks
 *  break (E-10-adjacent: entities still take damage, handled by the caller). */
export function explodeAt(world: Explodable, cx: number, cy: number, cz: number, power: number, mobGriefing: boolean): Set<string> {
  const affected = new Set<string>();
  if (!mobGriefing) return affected;
  for (let dx = 0; dx < 16; dx++) {
    for (let dy = 0; dy < 16; dy++) {
      for (let dz = 0; dz < 16; dz++) {
        // Only the boundary cells of the 16³ grid → an even spread of ray directions.
        if (dx !== 0 && dx !== 15 && dy !== 0 && dy !== 15 && dz !== 0 && dz !== 15) continue;
        let vx = (dx / 15) * 2 - 1;
        let vy = (dy / 15) * 2 - 1;
        let vz = (dz / 15) * 2 - 1;
        const len = Math.hypot(vx, vy, vz) || 1;
        vx /= len; vy /= len; vz /= len;
        let f = power * 0.9;
        let px = cx, py = cy, pz = cz;
        while (f > 0) {
          const bx = Math.floor(px), by = Math.floor(py), bz = Math.floor(pz);
          if (world.getBlockState(bx, by, bz) !== 0) {
            const res = world.blastResistance(bx, by, bz);
            f -= (Math.min(res, 20) + 0.3) * 0.3;
            if (f > 0) affected.add(`${bx},${by},${bz}`);
          }
          px += vx * STEP; py += vy * STEP; pz += vz * STEP;
          f -= TRAVEL_DECAY;
        }
      }
    }
  }
  for (const k of affected) {
    const [bx, by, bz] = k.split(',').map(Number) as [number, number, number];
    if (world.blastResistance(bx, by, bz) === Infinity) continue;
    world.breakBlock(bx, by, bz);
  }
  return affected;
}
